import type { AnyBlock } from "@/types";
import { useRenderer } from "../context";

/** CD "A note from the studio" — a 2-col intro: a bordered author card on the
 *  left, the studio note on the right, with the heading personalised to the
 *  client's first name. Fixed-layout designed section; copy comes from props
 *  (CD defaults below) so it can be made editable later. */
interface CdIntroProps {
  eyebrow?: string;
  heading?: string; // composed as "{heading}, {firstName}."
  about_eyebrow?: string;
  paragraphs?: string[];
  author_name?: string;
  author_role?: string;
  author_email?: string;
  author_photo_url?: string;
}

const DEFAULTS: Required<CdIntroProps> = {
  eyebrow: "A note from the studio",
  heading: "Let's create something great together",
  about_eyebrow: "A little about us",
  paragraphs: [
    "In the 15 years since Arkitectly was founded, we have helped hundreds of people realise their dreams for their homes — from small extensions to large renovations, from terraced city houses to listed country cottages.",
    "We aim to offer a personal service that you won't always find elsewhere, and with our extensive design experience we add value to the process by suggesting tweaks you may not have thought of to help you get the most from your extension.",
    "If you have any questions about this quote, or the process itself, please don't hesitate to contact us.",
  ],
  author_name: "Kate Hughes",
  author_role: "Director",
  author_email: "hello@arkitectly.co.uk",
  author_photo_url: "/cd/director.png",
};

export function CdIntroBlock({ block }: { block: AnyBlock }) {
  const { quote } = useRenderer();
  const p = (block.props ?? {}) as CdIntroProps;
  const firstName = quote?.contact_first_name?.trim();
  const headingBase = p.heading ?? DEFAULTS.heading;
  const heading = firstName ? `${headingBase}, ${firstName}.` : `${headingBase}.`;
  const paragraphs = p.paragraphs ?? DEFAULTS.paragraphs;
  const photo = p.author_photo_url || DEFAULTS.author_photo_url;
  const email = p.author_email ?? DEFAULTS.author_email;

  return (
    <section className="block cd-intro">
      <div className="cd-intro__head">
        <div className="cd-intro__spacer" />
        <div>
          <p className="eyebrow">{p.eyebrow ?? DEFAULTS.eyebrow}</p>
          <h2 className="cd-intro__title">{heading}</h2>
        </div>
      </div>
      <div className="cd-intro__body">
        <aside className="cd-intro__card">
          {photo ? (
            <img className="cd-intro__photo" src={photo} alt={p.author_name ?? DEFAULTS.author_name} loading="lazy" />
          ) : (
            <span className="cd-intro__photo cd-intro__photo--ph" aria-hidden="true" />
          )}
          <div className="cd-intro__name">{p.author_name ?? DEFAULTS.author_name}</div>
          <div className="cd-intro__role small">{p.author_role ?? DEFAULTS.author_role}</div>
          <hr />
          <a className="cd-intro__email" href={`mailto:${email}`}>{email}</a>
        </aside>
        <div className="cd-intro__about">
          <p className="eyebrow cd-intro__about-eyebrow">{p.about_eyebrow ?? DEFAULTS.about_eyebrow}</p>
          {paragraphs.map((para, i) => (
            <p className="cd-intro__p" key={i}>{para}</p>
          ))}
        </div>
      </div>
    </section>
  );
}
